<!-- @format -->

<template>
	<pc-layout-main-container :class="['pc-table-wrap', isTTB ? 'pc-table-wrap-ttb' : '']" :gap-placement="gapPlacement">
		<template v-if="isHeadersShow" #content-header>
			<pc-table-header
				v-model="searchValue"
				:table-ref="tableRef"
				:search-span="searchSpan"
				:search-list="searchList"
				:more-search-list="moreSearchList"
				:default-check-more-search-list="defaultCheckMoreSearchList"
				:can-more-search="canMoreSearch"
				:can-add-more-search="canAddMoreSearch"
				:can-comment-search="canCommentSearch"
				:comment-search-place-holder="commentSearchPlaceHolder"
				@on-search="$emit('on-search', $event)"
				@on-search-reset="$emit('on-search-reset')"
			>
				<template #search-comment-front="scope">
					<slot name="search-comment-front" v-bind="scope"></slot>
				</template>

				<template #search-comment-backend="scope">
					<slot name="search-comment-backend" v-bind="scope"></slot>
				</template>

				<template #search-cpt-front="scope">
					<slot name="search-cpt-front" v-bind="scope"></slot>
				</template>

				<template #search-cpt="scope">
					<slot name="search-cpt" v-bind="scope"></slot>
				</template>

				<template #search-cpt-backend="scope">
					<slot name="search-cpt-backend" v-bind="scope"></slot>
				</template>

				<template #check-search-item="scope">
					<slot name="check-search-item" v-bind="scope"></slot>
				</template>
			</pc-table-header>
		</template>
		<template #content-main>
			<pc-table
				ref="pcTableRef"
				v-model:page-value="pageValue"
				:data="data"
				:column-list="columnList"
				:default-columns-list="defaultColumnsList"
				:default-check-columns-list="defaultCheckColumnsList"
				:loading="loading"
				:total="total"
				:show-total="showTotal"
				:can-import="canImport"
				:can-export="canExport"
				:can-add-more-column="canAddMoreColumn"
				:multiple="multiple"
				:selectable="selectable"
				:index="index"
				:import-props="importProps"
				:auto-save-column-prop="autoSaveColumnProp"
				:export-props="exportProps"
				:pagination-props="paginationProps"
				:setting-column-show-length="settingColumnShowLength"
				:setting-column-config="settingColumnConfig"
				:action-btn-config="actionBtnConfig"
				:export-column-list="exportColumnList"
				v-bind="$attrs"
			>
				<template #title-front>
					<slot name="title-front"></slot>
				</template>

				<template #button-front>
					<slot name="button-front"></slot>
				</template>

				<template #button-backend>
					<slot name="button-backend"></slot>
				</template>

				<template #check-column-item="scope">
					<slot name="check-column-item" v-bind="scope"></slot>
				</template>

				<template v-for="itm in defaultColumnsList" :key="itm.code" #[`column-header-${itm.code}`]="scope">
					<slot :name="`column-header-${itm.code}`" v-bind="scope"></slot>
				</template>

				<template v-for="itm in defaultColumnsList" :key="itm.code" #[`column-default-${itm.code}`]="scope">
					<slot :name="`column-default-${itm.code}`" v-bind="scope"></slot>
				</template>

				<template v-for="itm in columnList" :key="itm.code" #[`column-header-${itm.code}`]="scope">
					<slot :name="`column-header-${itm.code}`" v-bind="scope"></slot>
				</template>

				<template v-for="itm in columnList" :key="itm.code" #[`column-default-${itm.code}`]="scope">
					<slot :name="`column-default-${itm.code}`" v-bind="scope"></slot>
				</template>

				<template #column-header-column-setting="scope">
					<slot name="column-header-column-setting" v-bind="scope"></slot>
				</template>

				<template #column-default-column-setting="scope">
					<slot name="column-default-column-setting" v-bind="scope"></slot>
				</template>

				<template #import-top>
					<slot name="import-top"></slot>
				</template>
				<template #import-bottom>
					<slot name="import-bottom"></slot>
				</template>
			</pc-table>
		</template>
	</pc-layout-main-container>
</template>

<script setup lang="ts">
	import type { ComponentType, SettingColumnConfigType, ExportProps, ActionBtnsConfigType } from './types';
	import PcTableHeader from '@/components/pc-table/pc-table-header.vue';
	import PcTable from '@/components/pc-table/pc-table.vue';
	import { PcLayoutMainContainer } from '@/layout';
	import { type CheckGroupList } from '@/components/pc-check-box-list/types';

	interface Props {
		searchValue?: Record<string, any>; // 查询条件默认值
		searchSpan?: number; // 筛选条件span占比
		searchList?: ComponentType[]; // 查询条件字段列表
		moreSearchList?: CheckGroupList; // 更多筛选字段列表
		defaultCheckMoreSearchList?: string[]; // 默认选中的更多筛选字段
		canCommentSearch?: boolean; // 是否有全局搜索框
		commentSearchPlaceHolder?: string; // 全局搜索框提示信息
		canMoreSearch?: boolean; // 可展开更多筛选框
		canAddMoreSearch?: boolean; // 可添加更多筛选字段

		data: any[];
		defaultColumnsList?: ComponentType[];
		columnList: ComponentType[];
		defaultCheckColumnsList?: string[];
		loading?: boolean; // 是否加载
		total?: number; // 总数
		showTotal?: boolean;
		canImport?: boolean;
		canExport?: boolean;
		canAddMoreColumn?: boolean;
		multiple?: boolean;
		index?: boolean;
		selectable?: (row: any, index: number) => boolean;
		importProps?: Record<string, any>;
		exportProps?: {
			searchExport?: ExportProps;
			sheetExport?: ExportProps;
			allExport?: ExportProps;
			selectExport?: ExportProps;
		};
		paginationProps?: Record<string, any>;
		settingColumnShowLength?: number;
		settingColumnConfig?: SettingColumnConfigType[];
		pageValue?: {
			pageSize: number;
			pageNum: number;
		};
		actionBtnConfig?: ActionBtnsConfigType[];
		exportColumnList?: CheckGroupList;
		gapPlacement?: any;
		autoSaveColumnProp?: {
			canAutoSave: boolean;
			module: string;
			page: string;
		};
	}
	const props = withDefaults(defineProps<Props>(), {
		searchSpan: 6,
		searchList: () => [],
	});

	const emits = defineEmits(['on-search', 'on-search-reset', 'update:searchValue', 'update:pageValue']);

	const pcTableRef = ref<InstanceType<typeof PcTable>>();
	const tableRef = computed(() => pcTableRef.value?.tableRef);

	const isHeadersShow = computed(() => props.searchList.length || props.canCommentSearch);

	const isTTB = computed(() => props.gapPlacement?.includes('ttb'));

	const searchValue = computed({
		get() {
			return props.searchValue || {};
		},
		set(val) {
			emits('update:searchValue', val);
		},
	});

	const pageValue = computed({
		get() {
			return props.pageValue;
		},
		set(val) {
			emits('update:pageValue', val);
		},
	});

	defineExpose({ tableRef, pcTableRef });
</script>
<style lang="scss" scoped>
	// .pc-table-wrap > :deep(.pc-layout-main-container-content > .pc-layout-main-container-content-main) {
	// 	padding: 16px 0 0 0 !important;
	// }

	// .pc-table-wrap > :deep(.pc-layout-main-container-content > .el-divider--horizontal) {
	// 	margin: 0 !important;
	// }

	// .pc-table-wrap-ttb > :deep(.pc-layout-main-container-content > .pc-layout-main-container-content-header) {
	// 	padding: 16px 0 !important;
	// }

	// .pc-table-wrap-ttb > :deep(.pc-layout-main-container-content > .pc-layout-main-container-content-main) {
	// 	padding: 16px 0 !important;
	// }

	.pc-table-wrap-ttb {
		:deep(.pc-table-header) {
			// padding: 0 32px !important;
			margin-bottom: -16px !important;
		}

		// :deep(.pc-table-btns) {
		// 	padding: 0 32px !important;
		// }

		// :deep(.pc-table-content) {
		// 	padding: 0 32px !important;
		// }
	}
</style>

<style scoped>
	.pagination {
		justify-content: flex-end;
	}
</style>
